import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Film, Sparkles, Layers } from "lucide-react";
import { useContent } from "../../context/ContentContext";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";
import VideoGallery from "../../components/VideoGallery";

// Neutral SVG placeholder for missing artworks (data URI, dark cinema tone)
const PLACEHOLDER = (title) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1280 720'>
      <defs>
        <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
          <stop offset='0' stop-color='#1a1a1a'/>
          <stop offset='1' stop-color='#0a0a0a'/>
        </linearGradient>
      </defs>
      <rect width='1280' height='720' fill='url(#g)'/>
      <text x='50%' y='50%' font-family='Bebas Neue, sans-serif' font-size='96' fill='#ff8a3c' opacity='0.9' text-anchor='middle' dominant-baseline='middle' letter-spacing='6'>${title}</text>
      <text x='50%' y='62%' font-family='monospace' font-size='24' fill='#ffffff' opacity='0.35' text-anchor='middle' letter-spacing='8'>KILOMBO · CINE</text>
    </svg>`
  )}`;

const IMG = {
  CABALLEROS: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/042kbbty_Los%20caballeros%20las%20prefieren%20brutas.png",
  VERASUR: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/xpd09k90_Verasur.png",
  BRASIL: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/68nz18al_Yo%20te%20llevo%20a%20Brasil-Teaser.png",
  VOZKIDS: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/ma13sx5q_La%20voz%20kids%202015.png",
  DESAFIO: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/75s6z8z4_Desaf%C3%ADo%20India%202015.png",
  FORTUNA: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/8v4qz3r2_Fortuna%20Lake%20Teaser.png",
  DOBLE: "https://customer-assets-7cd3h4nn.emergentagent.net/job_team-workspace-60/artifacts/2xsamgvt_DOBLE%20-%20Trailer%20Oficial.png",
};

const GALLERY_GROUPS = [
  {
    category: "CINE",
    items: [
      { brand: "Verasur", activo: "Cine", image: IMG.VERASUR, video: "https://vimeo.com/83863807" },
    ],
  },
  {
    category: "PELÍCULA · SERIE",
    items: [
      { brand: "Los caballeros las prefieren brutas", activo: "Serie", image: IMG.CABALLEROS, video: "https://vimeo.com/83863130" },
      { brand: "Yo te llevo a Brasil — Teaser", activo: "Película", image: IMG.BRASIL, video: "https://vimeo.com/112824865" },
      { brand: "Fortuna Lake — Teaser", activo: "Película", image: IMG.FORTUNA, video: "https://vimeo.com/229356634" },
      { brand: "DOBLE — Trailer Oficial", activo: "Película", image: IMG.DOBLE, video: "https://vimeo.com/317271723" },
    ],
  },
  {
    category: "TV",
    items: [
      { brand: "La voz kids 2015", activo: "TV", image: IMG.VOZKIDS, video: "https://vimeo.com/157445489" },
      { brand: "Desafío India 2015", activo: "TV", image: IMG.DESAFIO, video: "https://vimeo.com/157449443" },
    ],
  },
];

const PILLARS = [
  { Icon: Film, title: "Música original", text: "Componemos cada score a la medida del guion — desde la melodía principal hasta cada motivo temático." },
  { Icon: Layers, title: "Diseño de sonido", text: "Sound design y foley que detonan emociones para cada escena." },
  { Icon: Sparkles, title: "Mezcla Dolby Atmos", text: "Mezcla inmersiva final para cine, streaming y broadcast. Todo bajo un mismo techo." },
];

export function SeriesCineTVPage() {
  const { content, loading } = useContent();
  if (loading || !content) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-white/60 font-mono-caps tracking-widest">CARGANDO...</div>;
  }
  return (
    <div className="relative min-h-screen bg-black text-white overflow-x-hidden">
      <Header />

      {/* HERO — texto + video (like Audio Branding) */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-16 md:pt-32 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=1800&q=85" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#ff6a2b]/12 to-black" />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-6 xl:col-span-6 min-w-0">
            <p className="font-mono-caps text-xs md:text-sm tracking-[0.3em] uppercase text-[#ff8a3c]">Kilombo Music</p>
            <h1 className="mt-6 font-display font-black text-[40px] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[64px] xl:text-[68px] max-w-3xl">
              Cada nota es parte del <span className="text-gradient-music">guión</span>.
            </h1>
            <div className="mt-6 max-w-xl space-y-4 text-white/85 text-base md:text-lg leading-snug">
              <p>Componemos y producimos la música que el director imagina para cada escena: la que sostiene la tensión y la que se queda mucho después de los créditos. Cada nota trabaja para la historia, nunca por encima de ella.</p>
              <p>Con más de 25 años en cine, series y televisión, entregamos todo bajo un mismo techo: de la primera idea musical a la mezcla final.</p>
              <p className="text-white font-medium">Música original, diseño de sonido y mezcla en <span className="text-[#ff8a3c]">Dolby Atmos</span>. Todo en un solo lugar.</p>
            </div>
            <div className="mt-8 md:mt-10">
              <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-[#ff6a2b] hover:bg-[#ff8a3c] text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase transition-colors">
                Hablemos
                <span className="w-7 h-7 rounded-full bg-black text-white flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            </div>
          </div>

          {/* Embedded Vimeo video */}
          <div className="lg:col-span-6 xl:col-span-6 min-w-0">
            <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/15 shadow-2xl bg-black">
              <iframe
                src="https://player.vimeo.com/video/1154114780?autoplay=1&muted=1&loop=1&background=0&byline=0&title=0&portrait=0&controls=1"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Kilombo Series, Cine y TV"
              />
            </div>
            <p className="mt-3 font-mono-caps text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/50 text-center lg:text-left">
              Reel · Series, Cine y TV
            </p>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative bg-black py-16 md:py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {PILLARS.map(({ Icon, title, text }, i) => (
            <div key={i} className="relative rounded-2xl border border-white/10 bg-neutral-950 p-6 md:p-8 hover:border-[#ff8a3c]/40 transition-colors overflow-hidden">
              <div className="absolute -top-16 -right-16 w-44 h-44 rounded-full bg-[#ff6a2b]/15 blur-3xl pointer-events-none" />
              <div className="relative">
                <span className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#ffa957] to-[#ff6a2b] flex items-center justify-center shadow-lg shadow-[#ff6a2b]/20 mb-6">
                  <Icon className="w-5 h-5 text-black" />
                </span>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white leading-tight pt-1">{title}</h3>
                <p className="mt-4 text-white/70 text-base leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="relative bg-neutral-950 py-20 md:py-28 px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-caps text-xs tracking-[0.3em] uppercase text-white/40 mb-4">— Portafolio —</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-4xl">
            <span className="text-gradient-music">Historias</span> que suenan
          </h2>
          <p className="mt-5 max-w-2xl text-white/60 text-base md:text-lg leading-relaxed">
            Una selección de scores, teasers y trailers en los que hemos participado.
          </p>
          <div className="mt-14 md:mt-20">
            <VideoGallery groups={GALLERY_GROUPS} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-black py-20 md:py-28 px-6">
        <div className="max-w-4xl mx-auto text-center rounded-3xl border border-[#ff6a2b]/30 bg-gradient-to-br from-[#ff6a2b]/12 via-black to-black p-8 md:p-16">
          <h2 className="font-display font-black text-3xl md:text-4xl lg:text-5xl leading-tight text-white">
            ¿Cómo quieres que suene tu <span className="text-gradient-music">historia</span>?
          </h2>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-xl mx-auto">
            Score, sound design y mezcla en Dolby Atmos — todo en un solo estudio.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-shine inline-flex items-center gap-3 rounded-full bg-white text-black pl-6 pr-5 py-3.5 font-mono-caps text-xs tracking-[0.2em] uppercase hover:bg-neutral-100 transition-colors">
              Hablemos
              <span className="w-7 h-7 rounded-full bg-[#ff6a2b] text-black flex items-center justify-center"><ArrowRight className="w-3.5 h-3.5" /></span>
            </Link>
          </div>
        </div>
      </section>

      <Footer footer={content.footer} />
      <WhatsAppButton />
    </div>
  );
}

